import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useReceptors } from 'src/pages/Receptors/receptors.hooks'
import {
  useIsShowModal,
  useHideModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import { ModalForm, ModalFormId } from 'src/components/ModalForms/ModalForm'
import { useControlModel } from 'src/components/async/model/control/useControl'
import { useSwrGrpc } from 'src/components/async/useSwrGrpc'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown'
import type { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { isGrpcError } from 'src/Utils/isGrpcError'
import { useCurrentReceptor } from 'src/context/FormContext/CurrentReceptorContext'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { useInvalidateReceptorsCache } from 'src/components/async/model/receptor/useReceptors'
import { useInvalidateDocumentRequestsCache } from 'src/components/async/DocumentRequest/useDocumentRequests'
import { ReceptorPromiseClient } from '@trustero/trustero-api-web/lib/agent/receptor_grpc_web_pb'
import { UpdateConfigRequest } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { ReceptorUtilPromiseClient } from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_grpc_web_pb'
import { GetWorkflowUsersRequest } from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_pb'
import {
  getControls,
  getSources,
} from 'src/pages/Receptors/modals/ConfigureReceptorForm/ConfigureReceptorForm.helpers'
import {
  useCreateWorkflowTicket,
  WorkflowCredentials,
} from './AddWorkflowEvidence.hooks'
import {
  WorkflowFormState,
  AddWorkflowEvidenceForm,
  Option,
  NO_PRESET_VALUE,
} from './forms/AddWorkflowEvidenceForm'
import { AddWorkflowEvidenceFormDescription } from './forms/AddWorkflowEvidenceForm.components'
import { ModalSection } from './forms/AddWorkflowEvidenceForm.styles'
import {
  WORKFLOW_SYSTEM_NAMES,
  WorkflowSystemId,
} from './AddWorkflowEvidence.constants'

type AddWorkflowEvidenceModalProps = {
  controlId?: string
  requestId?: string
  requestDisplayId?: string
  requestControlTags?: string[]
  name: string
  openOnDrag?: boolean
  description?: string
} & (
  | { requestId: string; controlId?: never }
  | { controlId: string; requestId?: never }
)

type AdvanceMapping = {
  echoice_id?: string
  echoice_name?: string
  efield_id?: string
  efield_name?: string
  name?: string
  pid?: string
  pname?: string
  sfield_id?: string
  sfield_name?: string
  tid?: string
  tname?: string
  tpid?: string
}

type ConfigShape = {
  fetch_sources?: {
    advance_mappings?: AdvanceMapping[]
    project_mappings?: Array<{
      name?: string
      pid?: string
      pname?: string
      tid?: string
      tname?: string
      tpid?: string
    }>
  }
  [k: string]: unknown
}

export const AddWorkflowEvidenceModal = ({
  controlId,
  requestId,
  requestDisplayId,
  requestControlTags,
  name,
  openOnDrag = false,
  description,
}: AddWorkflowEvidenceModalProps): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_WORKFLOW_EVIDENCE)
  const isConfigureModalShown = useIsShowModal(ModalFormId.CONFIGURE_RECEPTOR)
  const openConfigureModal = useSetActiveModal(ModalFormId.CONFIGURE_RECEPTOR)
  const openWorkflowModal = useSetActiveModal(ModalFormId.ADD_WORKFLOW_EVIDENCE)
  const createWorkflowTicket = useCreateWorkflowTicket()
  const receptorClient = useAuthorizedGrpcClient(ReceptorPromiseClient)
  const invalidateReceptorsCache = useInvalidateReceptorsCache()
  const invalidateDocumentRequestsCache = useInvalidateDocumentRequestsCache()
  const { setReceptor } = useCurrentReceptor()

  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('')
  const [pendingConfigureReceptorId, setPendingConfigureReceptorId] =
    useState<string>('')
  const [formState, setFormState] = useState<WorkflowFormState | null>(null)
  const [ticketCreateError, setTicketCreateError] = useState('')
  const [isCreatingTicket, setIsCreatingTicket] = useState(false)
  const [noPresetInstanceId, setNoPresetInstanceId] = useState('')

  const hide = useHideModal({
    modalId: ModalFormId.ADD_WORKFLOW_EVIDENCE,
    onHide: () => {
      setSelectedWorkflow('')
      setPendingConfigureReceptorId('')
      setFormState(null)
      setTicketCreateError('')
      setIsCreatingTicket(false)
      setNoPresetInstanceId('')
    },
  })

  const _placeholderName = name
  const _placeholderReqest = requestId

  const { data: jiraReceptorsRes, isLoading: isJiraWorkflowReceptorLoading } =
    useReceptors('trr-jira-workflow', true)
  const jiraWorkflowReceptors = useMemo(
    () =>
      (jiraReceptorsRes?.getReceptorsList() ?? []).filter((r) =>
        r.getIsenabled(),
      ),
    [jiraReceptorsRes],
  )

  const { data: snowReceptorsRes, isLoading: isSnowWorkflowReceptorLoading } =
    useReceptors('trr-servicenow-workflow', true)
  const snowWorkflowReceptors = useMemo(
    () =>
      (snowReceptorsRes?.getReceptorsList() ?? []).filter((r) =>
        r.getIsenabled(),
      ),
    [snowReceptorsRes],
  )

  const isLoading = useMemo(
    () => isJiraWorkflowReceptorLoading || isSnowWorkflowReceptorLoading,
    [isJiraWorkflowReceptorLoading, isSnowWorkflowReceptorLoading],
  )

  const hasJiraWorkflowReceptor = jiraWorkflowReceptors.length > 0
  const hasSnowWorkflowReceptor = snowWorkflowReceptors.length > 0
  const hasMultipleWorkflows =
    hasSnowWorkflowReceptor && hasJiraWorkflowReceptor

  const workflowItems: SelectItem[] = useMemo(() => {
    const list: SelectItem[] = []
    if (hasJiraWorkflowReceptor) {
      list.push({ name: 'Jira', value: 'trr-jira-workflow' })
    }
    if (hasSnowWorkflowReceptor) {
      list.push({ name: 'ServiceNow', value: 'trr-servicenow-workflow' })
    }
    return list
  }, [hasJiraWorkflowReceptor, hasSnowWorkflowReceptor])

  const workflowDefaultValues: SelectItem[] = useMemo(() => {
    if (!selectedWorkflow) return []
    const workflowName =
      selectedWorkflow === 'trr-jira-workflow' ? 'Jira' : 'ServiceNow'
    return [{ name: workflowName, value: selectedWorkflow }]
  }, [selectedWorkflow])

  useEffect(() => {
    if (selectedWorkflow || isLoading) return

    if (hasJiraWorkflowReceptor && !hasSnowWorkflowReceptor) {
      setSelectedWorkflow('trr-jira-workflow')
    } else if (!hasJiraWorkflowReceptor && hasSnowWorkflowReceptor) {
      setSelectedWorkflow('trr-servicenow-workflow')
    }
  }, [
    hasJiraWorkflowReceptor,
    hasSnowWorkflowReceptor,
    selectedWorkflow,
    isLoading,
  ])

  useEffect(() => {
    setTicketCreateError('')
  }, [selectedWorkflow])

  const selectedWorkflowReceptors = useMemo(() => {
    if (selectedWorkflow === 'trr-jira-workflow') return jiraWorkflowReceptors
    if (selectedWorkflow === 'trr-servicenow-workflow') {
      return snowWorkflowReceptors
    }
    return []
  }, [selectedWorkflow, jiraWorkflowReceptors, snowWorkflowReceptors])

  const getInstanceDisplayName = useCallback(
    (receptorId: string, tenantId: string, receptorName: string): string => {
      const cleanName = receptorName.trim()
      if (cleanName) return cleanName
      const cleanTenant = tenantId.trim()
      if (cleanTenant) return cleanTenant
      return `Instance ${receptorId.slice(-6)}`
    },
    [],
  )

  const resolveCreds = (cred: WorkflowCredentials) => {
    const site = (cred?.Site ?? cred?.site ?? '').trim()
    const label = (cred?.Label ?? cred?.label ?? '').trim()
    return { site, label }
  }

  const [submitText, setSubmitText] = useState<string>('Continue')
  useEffect(() => {
    const systemName =
      WORKFLOW_SYSTEM_NAMES[selectedWorkflow as WorkflowSystemId] || ''
    if (selectedWorkflow === 'trr-jira-workflow') {
      setSubmitText('Create in Jira')
    } else if (systemName) {
      setSubmitText(`Continue in ${systemName}`)
    } else {
      setSubmitText('Continue')
    }
  }, [selectedWorkflow])

  const { data: control } = useControlModel(controlId ?? '', !!controlId)
  const resolvedControlId = useMemo(
    () => control?.getModelId?.() || controlId || '',
    [control, controlId],
  )
  const controlRequiredEvidence = useMemo(() => {
    return control?.getSuggestedEvidence?.()?.trim() || ''
  }, [control])
  const initialTitle = useMemo(() => {
    const normalizedControlTags = Array.from(
      new Set(
        (requestControlTags || [])
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0),
      ),
    )
    const controlTagPrefix =
      normalizedControlTags.length > 0
        ? `${normalizedControlTags.map((tag) => `[${tag}]`).join(' ')} `
        : ''
    if (requestId) {
      const requestName = name?.trim() || 'Request for evidence'
      const displayId = requestDisplayId?.trim()
      const ticketPrefix = displayId ? `[${displayId}] ` : ''
      return `${ticketPrefix}${controlTagPrefix}${requestName}`.trim()
    }
    return resolvedControlId
      ? `[${resolvedControlId}] Request for evidence`
      : 'Request for evidence'
  }, [requestId, name, requestDisplayId, requestControlTags, resolvedControlId])

  const configToOptions = useCallback((configRaw?: string): Option[] => {
    try {
      const cfg = (
        typeof configRaw === 'string' ? JSON.parse(configRaw) : configRaw ?? {}
      ) as {
        fetch_sources?: { project_mappings?: Record<string, string>[] }
      }
      const raw = cfg.fetch_sources?.project_mappings ?? []
      return raw.map((pm) => ({
        name: pm.name ?? '',
        pid: pm.pid ?? '',
        pname: pm.pname ?? '',
        tid: pm.tid ?? '',
        tname: pm.tname ?? '',
        tpid: pm.tpid ?? '',
      }))
    } catch {
      return []
    }
  }, [])

  const upsertProjectMappingConfig = useCallback(
    (
      configRaw: string | undefined,
      mapping: {
        pid: string
        pname: string
        tid: string
        tname: string
        tpid?: string
        name?: string
      },
    ): string => {
      let cfg: ConfigShape = {}
      try {
        cfg = configRaw ? (JSON.parse(configRaw) as ConfigShape) ?? {} : {}
      } catch {
        cfg = {}
      }

      const fetchSources = { ...(cfg.fetch_sources || {}) }
      const existing = Array.isArray(fetchSources.project_mappings)
        ? [...fetchSources.project_mappings]
        : []
      const existingMatch = existing.find(
        (pm) => pm.pid === mapping.pid && pm.tid === mapping.tid,
      )
      const deduped = existing.filter(
        (pm) => pm.pid !== mapping.pid || pm.tid !== mapping.tid,
      )
      const maxSavedPresetIndex = deduped.reduce((max, pm) => {
        const rawName = (pm.name || '').trim()
        const match = /^Saved Preset (\d+)$/i.exec(rawName)
        if (!match) return max
        const parsed = Number(match[1])
        return Number.isFinite(parsed) ? Math.max(max, parsed) : max
      }, 0)

      const pname = mapping.pname || mapping.pid
      const tname = mapping.tname || mapping.tid
      const presetName =
        mapping.name?.trim() ||
        existingMatch?.name?.trim() ||
        `Saved Preset ${maxSavedPresetIndex + 1}`
      deduped.push({
        name: presetName,
        pid: mapping.pid,
        pname,
        tid: mapping.tid,
        tname,
        tpid: mapping.tpid || mapping.pid,
      })

      return JSON.stringify({
        ...cfg,
        fetch_sources: {
          ...fetchSources,
          project_mappings: deduped,
        },
      })
    },
    [],
  )

  const getTitleFieldFromAdvanceMappings = (
    configRaw?: string | unknown,
    tableId?: string,
  ): string | undefined => {
    try {
      const cfg: ConfigShape =
        (typeof configRaw === 'string' ? JSON.parse(configRaw) : configRaw) ??
        {}

      const mappings = cfg.fetch_sources?.advance_mappings
      if (!Array.isArray(mappings) || mappings.length === 0) return undefined

      const normTable = (tableId ?? '').trim().toLowerCase()
      const match =
        mappings.find(
          (m) => (m.tid ?? '').trim().toLowerCase() === normTable,
        ) ?? (normTable ? undefined : mappings[0])

      if (!match) return undefined

      return match.sfield_id?.trim() || undefined
    } catch {
      return undefined
    }
  }

  const receptorsById = useMemo(() => {
    const pairs = selectedWorkflowReceptors.map((r) => [r.getId(), r] as const)
    return new Map<string, (typeof selectedWorkflowReceptors)[number]>(pairs)
  }, [selectedWorkflowReceptors])

  const selectionOptions = useMemo(
    () =>
      selectedWorkflowReceptors.flatMap((receptor) => {
        const receptorDisplayName = getInstanceDisplayName(
          receptor.getId(),
          receptor.getTenantid(),
          receptor.getName(),
        )
        return configToOptions(receptor.getConfig()).map((opt) => ({
          ...opt,
          receptorId: receptor.getId(),
          receptorDisplayName,
        }))
      }),
    [selectedWorkflowReceptors, configToOptions, getInstanceDisplayName],
  )

  const selectionOptionsWithNoPreset = useMemo(() => {
    if (selectedWorkflow !== 'trr-jira-workflow') return selectionOptions
    const noPresetOption: Option = {
      name: 'No Preset',
      pid: '',
      pname: '',
      tid: '',
      tname: '',
      tpid: '',
      receptorId: NO_PRESET_VALUE,
      receptorDisplayName: '',
    }
    return [...selectionOptions, noPresetOption]
  }, [selectedWorkflow, selectionOptions])

  const isManualNoPreset = !!formState?.useNoPreset
  const noPresetMode = selectionOptions.length === 0 || isManualNoPreset

  const noPresetInstanceItems: SelectItem[] = useMemo(
    () =>
      selectedWorkflowReceptors.map((receptor) => ({
        name: getInstanceDisplayName(
          receptor.getId(),
          receptor.getTenantid(),
          receptor.getName(),
        ),
        value: receptor.getId(),
      })),
    [selectedWorkflowReceptors, getInstanceDisplayName],
  )

  useEffect(() => {
    if (!noPresetMode) return
    if (noPresetInstanceId) return
    if (noPresetInstanceItems.length === 1) {
      setNoPresetInstanceId(String(noPresetInstanceItems[0].value ?? ''))
    }
  }, [noPresetMode, noPresetInstanceId, noPresetInstanceItems])

  const noPresetInstanceDefaultValues: SelectItem[] = useMemo(() => {
    if (!noPresetInstanceId) return []
    const selected = noPresetInstanceItems.find(
      (item) => String(item.value) === noPresetInstanceId,
    )
    return selected ? [selected] : []
  }, [noPresetInstanceId, noPresetInstanceItems])

  const noPresetSelectedReceptor = useMemo(() => {
    if (!noPresetInstanceId) return undefined
    return selectedWorkflowReceptors.find(
      (r) => r.getId() === noPresetInstanceId,
    )
  }, [noPresetInstanceId, selectedWorkflowReceptors])

  const noPresetProjectItems = useMemo(
    () =>
      noPresetSelectedReceptor
        ? getControls(noPresetSelectedReceptor.getConfig(), 'projects')
        : [],
    [noPresetSelectedReceptor],
  )

  const noPresetTaskTypeItems = useMemo(() => {
    if (!noPresetSelectedReceptor || !formState?.project) return []
    const taskTypes = getSources(
      noPresetSelectedReceptor.getConfig(),
      'task_types',
    )
    const selectedProject = noPresetProjectItems.find(
      (project) => String(project.value) === formState.project,
    )
    const childIds = selectedProject?.childIds
    if (!Array.isArray(childIds)) return []
    return taskTypes.filter((taskType) =>
      childIds.includes(String(taskType.value)),
    )
  }, [noPresetSelectedReceptor, noPresetProjectItems, formState?.project])

  const noPresetProjectDefaultValues: SelectItem[] = useMemo(() => {
    if (!formState?.project) return []
    const selected = noPresetProjectItems.find(
      (project) => String(project.value) === formState.project,
    )
    return selected ? [selected] : []
  }, [formState?.project, noPresetProjectItems])

  const noPresetTaskTypeDefaultValues: SelectItem[] = useMemo(() => {
    if (!formState?.taskType) return []
    const selected = noPresetTaskTypeItems.find(
      (taskType) => String(taskType.value) === formState.taskType,
    )
    return selected ? [selected] : []
  }, [formState?.taskType, noPresetTaskTypeItems])

  const selectedReceptor = useMemo(() => {
    if (!formState?.receptorId) return undefined
    return receptorsById.get(formState.receptorId)
  }, [formState?.receptorId, receptorsById])

  const selectedWorkflowCreds = useMemo(() => {
    if (!selectedReceptor) return { site: '', label: '', invalid: false }
    try {
      const raw = selectedReceptor.getCredentials?.()
      const creds = raw ? (JSON.parse(raw) as WorkflowCredentials) : {}
      const resolved = resolveCreds(creds)
      return { ...resolved, invalid: false }
    } catch {
      return { site: '', label: '', invalid: true }
    }
  }, [selectedReceptor])

  const assigneeReq = useMemo(() => {
    if (
      !show ||
      selectedWorkflow !== 'trr-jira-workflow' ||
      !formState?.receptorId
    ) {
      return null
    }
    const req = new GetWorkflowUsersRequest()
    req.setReceptorId(formState.receptorId)
    if (formState.project) {
      req.setProjectId(formState.project)
    }
    req.setMaxResults(100)
    return req
  }, [show, selectedWorkflow, formState?.receptorId, formState?.project])

  const { response: assigneesResponse } = useSwrGrpc(
    ReceptorUtilPromiseClient.prototype.getWorkflowUsers,
    assigneeReq as GetWorkflowUsersRequest,
    !!assigneeReq,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  )

  const isAssigneesLoading = useMemo(
    () =>
      !!assigneeReq &&
      (Boolean(assigneesResponse?.isLoading) ||
        Boolean(assigneesResponse?.isValidating)),
    [
      assigneeReq,
      assigneesResponse?.isLoading,
      assigneesResponse?.isValidating,
    ],
  )

  const assigneeItems: SelectItem[] = useMemo(() => {
    const users = assigneesResponse?.data?.getUsersList() ?? []
    return users.map((user) => {
      const email = user.getEmail()
      const display = user.getDisplayName()
      return {
        name: email ? `${display} (${email})` : display,
        value: user.getId(),
      }
    })
  }, [assigneesResponse?.data])

  const configureActions = useMemo(
    () =>
      selectedWorkflowReceptors.map((receptor) => ({
        receptorId: receptor.getId(),
        label: `Create preset for ${getInstanceDisplayName(
          receptor.getId(),
          receptor.getTenantid(),
          receptor.getName(),
        )} instance`,
      })),
    [selectedWorkflowReceptors, getInstanceDisplayName],
  )

  useEffect(() => {
    if (!pendingConfigureReceptorId) return
    if (isConfigureModalShown) return
    openWorkflowModal()
    setPendingConfigureReceptorId('')
  }, [pendingConfigureReceptorId, isConfigureModalShown, openWorkflowModal])

  const onConfigureAction = useCallback(
    (receptorId: string) => {
      const receptor = selectedWorkflowReceptors.find(
        (item) => item.getId() === receptorId,
      )
      if (!receptor) return
      setPendingConfigureReceptorId(receptorId)
      setReceptor(receptor.toObject())
      openConfigureModal()
    },
    [selectedWorkflowReceptors, setReceptor, openConfigureModal],
  )

  const onConfigureFlavorLink = useCallback(
    (receptorId: string) => {
      onConfigureAction(receptorId)
    },
    [onConfigureAction],
  )

  const onSubmitFunc = async (data: WorkflowFormState) => {
    const {
      project,
      taskType,
      title,
      description: ticketDescription,
      assigneeId,
    } = data

    if (!data.receptorId) {
      showInfoToast('Please select a valid workflow preset.')
      return
    }

    const receptorForConfig = receptorsById.get(data.receptorId)
    if (!receptorForConfig) {
      showInfoToast('Selected workflow preset is no longer available.')
      return
    }

    if (data.useNoPreset && data.saveAsPreset) {
      if (!project || !taskType) {
        showInfoToast(
          'Please choose project and task type before saving preset.',
        )
        return
      }
      const selectedProject = noPresetProjectItems.find(
        (item) => String(item.value) === project,
      )
      const selectedTaskType = noPresetTaskTypeItems.find(
        (item) => String(item.value) === taskType,
      )
      const selectedProjectName =
        typeof selectedProject?.name === 'string' && selectedProject.name.trim()
          ? selectedProject.name
          : project
      const selectedTaskTypeName =
        typeof selectedTaskType?.name === 'string' &&
        selectedTaskType.name.trim()
          ? selectedTaskType.name
          : data.taskName || taskType

      const request = new UpdateConfigRequest()
        .setReceptorId(receptorForConfig.getId())
        .setName(receptorForConfig.getName() || '')
        .setDescription(receptorForConfig.getDescription() || '')
        .setConfig(
          upsertProjectMappingConfig(receptorForConfig.getConfig(), {
            pid: project,
            pname: selectedProjectName,
            tid: taskType,
            tname: selectedTaskTypeName,
            tpid: selectedTaskType?.parentId || project,
          }),
        )

      try {
        await receptorClient.updateConfig(request)
        await invalidateReceptorsCache()
      } catch {
        showInfoToast('Failed to save workflow preset.')
        return
      }
    }

    let wfCredentials: WorkflowCredentials = {}
    let hasCredentialsParseError = false
    try {
      const raw = receptorForConfig?.getCredentials?.()
      if (raw) wfCredentials = JSON.parse(raw) as WorkflowCredentials
    } catch {
      hasCredentialsParseError = true
    }
    if (hasCredentialsParseError) {
      showInfoToast(
        'Workflow credentials are invalid. Please reconnect this integration.',
      )
      return
    }

    const { site: workflowSiteUrl, label: workflowLabel } =
      resolveCreds(wfCredentials)

    if (selectedWorkflow === 'trr-servicenow-workflow') {
      const inst = (workflowSiteUrl || '').trim()
      if (!inst) {
        showInfoToast(
          'ServiceNow instance URL is missing. Please update connector credentials.',
        )
        return
      }

      let host = inst
      if (/^https?:\/\//i.test(host)) {
        host = host.replace(/^https?:\/\/(.*?)(\/.*)?$/i, '$1')
      }

      const table = data.taskType || 'task'
      const credLabel = (workflowLabel || '').trim()
      const advTitleField = getTitleFieldFromAdvanceMappings(
        receptorForConfig?.getConfig(),
        table,
      )
      const fieldForTitle =
        advTitleField && advTitleField.trim()
          ? advTitleField.trim()
          : 'short_description'
      const inner = `${table}.do?sys_id=-1&sysparm_query=${fieldForTitle}=${title}^comments=[${credLabel}]`
      let encoded = encodeURIComponent(inner)
      encoded = encoded.replace(/%5E/g, '%255E')

      const url = `https://${host}/nav_to.do?uri=${encoded}`
      window.open(url, '_blank', 'noopener,noreferrer')
      hide()
      return
    }

    if (!workflowSiteUrl) {
      showInfoToast(
        'Jira site URL is missing. Please update connector credentials.',
      )
      return
    }

    if (!data.useNoPreset) {
      const selectedConfig = selectionOptions.find(
        (opt) =>
          opt.receptorId === data.receptorId &&
          opt.pid === project &&
          opt.tid === taskType,
      )
      if (!selectedConfig) {
        showInfoToast('Selected Jira preset is no longer available.')
        return
      }
    }

    if (!assigneeId?.trim()) {
      showInfoToast('Please select an assignee.')
      return
    }

    try {
      setIsCreatingTicket(true)
      setTicketCreateError('')
      const response = await createWorkflowTicket({
        receptorId: data.receptorId,
        projectId: project,
        taskTypeId: taskType,
        title: title.trim(),
        description: ticketDescription.trim(),
        label: workflowLabel,
        assigneeId: assigneeId.trim(),
        requestId: requestId || undefined,
      })
      const ticketUrl = response.getTicketUrl()
      const ticketKey = response.getTicketKey()
      showInfoToast(
        ticketKey ? `Created Jira ticket ${ticketKey}` : 'Created Jira ticket',
      )
      if (ticketUrl) {
        window.open(ticketUrl, '_blank', 'noopener,noreferrer')
      }
      if (requestId) {
        await invalidateDocumentRequestsCache()
      }
      hide()
    } catch (err) {
      setTicketCreateError(
        isGrpcError(err) ? err.message : 'Failed to create workflow ticket',
      )
    } finally {
      setIsCreatingTicket(false)
    }
  }

  const onContinueInJira = () => {
    if (selectedWorkflow !== 'trr-jira-workflow' || !formState) return

    const {
      project,
      taskType,
      title,
      description: ticketDescription,
    } = formState
    if (!formState.receptorId || !project || !taskType || !title?.trim()) {
      showInfoToast('Please complete the Jira task details first.')
      return
    }

    const receptorForConfig = receptorsById.get(formState.receptorId)
    if (!receptorForConfig) {
      showInfoToast('Selected workflow preset is no longer available.')
      return
    }

    let wfCredentials: WorkflowCredentials = {}
    try {
      const raw = receptorForConfig?.getCredentials?.()
      if (raw) wfCredentials = JSON.parse(raw) as WorkflowCredentials
    } catch {
      showInfoToast(
        'Workflow credentials are invalid. Please reconnect this integration.',
      )
      return
    }
    const { site: workflowSiteUrl, label: workflowLabel } =
      resolveCreds(wfCredentials)

    let site = (workflowSiteUrl || '').trim()
    if (!site) {
      showInfoToast(
        'Jira site URL is missing. Please update connector credentials.',
      )
      return
    }
    if (site && !/^https?:\/\//i.test(site)) site = `https://${site}`
    const base = `${site.replace(
      /\/$/,
      '',
    )}/secure/CreateIssueDetails!init.jspa`

    const params = new URLSearchParams()
    params.set('pid', String(project))
    params.set('issuetype', String(taskType))
    params.set('summary', title)
    if (ticketDescription) params.set('description', ticketDescription)
    if (workflowLabel) params.set('labels', workflowLabel)

    window.open(`${base}?${params.toString()}`, '_blank', 'noopener,noreferrer')
    hide()
  }

  const onSelectNoPresetInstance = useCallback((selected: SelectItem[]) => {
    const item = selected?.[0]
    const receptorId = item ? String(item.value ?? '') : ''
    setNoPresetInstanceId(receptorId)
    setTicketCreateError('')
    setFormState((prev) =>
      prev
        ? {
            ...prev,
            useNoPreset: true,
            receptorId,
            project: '',
            taskType: '',
            taskName: '',
          }
        : prev,
    )
  }, [])

  const onSelectNoPresetProject = useCallback((selected: SelectItem[]) => {
    const item = selected?.[0]
    const projectId = item ? String(item.value ?? '') : ''
    setTicketCreateError('')
    setFormState((prev) =>
      prev
        ? {
            ...prev,
            useNoPreset: true,
            project: projectId,
            taskType: '',
            taskName: '',
          }
        : prev,
    )
  }, [])

  const onSelectNoPresetTaskType = useCallback((selected: SelectItem[]) => {
    const item = selected?.[0]
    const taskTypeId = item ? String(item.value ?? '') : ''
    const taskTypeName = item ? String(item.name ?? '') : ''
    setTicketCreateError('')
    setFormState((prev) =>
      prev
        ? {
            ...prev,
            useNoPreset: true,
            taskType: taskTypeId,
            taskName: taskTypeName,
          }
        : prev,
    )
  }, [])

  useEffect(() => {
    if (!noPresetMode || !noPresetInstanceId) return
    setFormState((prev) =>
      prev
        ? {
            ...prev,
            useNoPreset: true,
            receptorId: noPresetInstanceId,
          }
        : prev,
    )
  }, [noPresetMode, noPresetInstanceId])

  const isJiraWorkflowSelected = selectedWorkflow === 'trr-jira-workflow'
  const disableContinueInJira =
    !formState?.receptorId ||
    !formState?.project ||
    !formState?.taskType ||
    !formState?.title?.trim()

  const disablePrimarySubmit = isJiraWorkflowSelected
    ? disableContinueInJira ||
      !formState?.assigneeId?.trim() ||
      isCreatingTicket
    : !selectionOptions.length

  return (
    <ModalForm
      formId={ModalFormId.ADD_WORKFLOW_EVIDENCE}
      title={'Create a Workflow Task for Evidence'}
      description={<AddWorkflowEvidenceFormDescription />}
      openOnDrag={openOnDrag}
      submitText={submitText}
      show={show}
      hide={hide}
      enforceFocus={false}
      showOverflow
      disableSubmitButton={disablePrimarySubmit}
      busy={isCreatingTicket}
      customActionButton={
        isJiraWorkflowSelected ? (
          <StandardButton
            variant={StandardButtonVariant.SECONDARY}
            buttonSize={StandardButtonSize.MEDIUM}
            onClick={onContinueInJira}
            disabled={disableContinueInJira}
          >
            Continue in Jira
          </StandardButton>
        ) : undefined
      }
    >
      {hasMultipleWorkflows && (
        <ModalSection>
          <SelectDropdown
            label="Workflow Integration"
            items={workflowItems}
            defaultValues={workflowDefaultValues}
            onSelectCb={([item]) =>
              setSelectedWorkflow(String(item.value ?? ''))
            }
            placeholder="Select workflow"
            hideError
          />
        </ModalSection>
      )}

      {isLoading ? (
        <ModalSection>
          <TableListLoader rows={1} />
        </ModalSection>
      ) : selectedWorkflow ? (
        <AddWorkflowEvidenceForm
          modalId={ModalFormId.ADD_WORKFLOW_EVIDENCE}
          onSubmitFunc={onSubmitFunc}
          isShown={show}
          controlId={resolvedControlId}
          siteUrl=""
          label=""
          name={WORKFLOW_SYSTEM_NAMES[selectedWorkflow as WorkflowSystemId]}
          configs={selectionOptionsWithNoPreset}
          description={description || controlRequiredEvidence || ''}
          configureActions={configureActions}
          onConfigureAction={onConfigureAction}
          onConfigureLinkClick={onConfigureFlavorLink}
          onStateChange={setFormState}
          showTicketDetails={isJiraWorkflowSelected}
          ticketDetailsLabel={selectedWorkflowCreds.label}
          assigneeItems={assigneeItems}
          assigneeLoading={isAssigneesLoading}
          requireAssignee={isJiraWorkflowSelected}
          ticketError={ticketCreateError}
          noPresetMode={noPresetMode}
          noPresetInstanceItems={noPresetInstanceItems}
          noPresetInstanceDefaultValues={noPresetInstanceDefaultValues}
          onSelectNoPresetInstance={onSelectNoPresetInstance}
          noPresetProjectItems={noPresetProjectItems}
          noPresetProjectDefaultValues={noPresetProjectDefaultValues}
          onSelectNoPresetProject={onSelectNoPresetProject}
          noPresetTaskTypeItems={noPresetTaskTypeItems}
          noPresetTaskTypeDefaultValues={noPresetTaskTypeDefaultValues}
          onSelectNoPresetTaskType={onSelectNoPresetTaskType}
          hasConfiguredPresets={selectionOptions.length > 0}
          initialTitle={initialTitle}
        />
      ) : null}
    </ModalForm>
  )
}
